import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '../../../../services/http.service';
import { NotificationService } from '../../../../services/notification.service';
import { debounceTime } from 'rxjs/operators';
import {PartRatePipe} from '../../../../pipes/part-rate.pipe';
import {Subscription} from 'rxjs';

interface Developer {
  id: any;
  caption: string;
  is_condition?: boolean;
}

@Component({
  selector: 'app-import-edit-realization',
  templateUrl: './import-edit-realization.component.html',
  styleUrls: ['./import-edit-realization.component.scss'],
  standalone: false
})
export class ImportEditRealizationComponent implements OnInit, OnDestroy {

  public feed_id: any;
  public data: any;
  public report: any;
  public formGroup: UntypedFormGroup;
  public loading = false;
  public complexes_id = [];
  public settings_id = [];

  public developerGroups: { active: Developer[]; inactive: Developer[] } = { active: [], inactive: [] };

  private _searchField = '';
  public get searchField() { return this._searchField; }
  public set searchField(value) {
    this._searchField = value;
    this.searchFieldIds = [];
    this.searchFieldError = false;

    if (!this.complexes?.controls) { return; }

    const searchLower = value.toLowerCase().trim();
    if (!searchLower) { return; }

    this.complexes.controls.forEach((complex) => {
      const caption = complex.get('caption')?.value;
      if (caption && typeof caption === 'string' && caption.toLowerCase().includes(searchLower)) {
        this.searchFieldIds.push(complex.get('id')?.value);
      }
    });

    if (!this.searchFieldIds.length) { this.searchFieldError = true; }
  }

  public searchFieldError = false;
  public searchFieldIds: string[] = [];

  private subscriptions: Subscription[] = [];

  constructor(
    public http: HttpClient,
    public fb: UntypedFormBuilder,
    public notify: NotificationService,
    public router: Router,
    public activatedRoute: ActivatedRoute,
    private partRate: PartRatePipe,
  ) {
    this.feed_id = activatedRoute.snapshot.parent.params['id'];
    this.data = activatedRoute.snapshot.data['data'];
    this.complexes_id = this.data['complexes'].map((item) => item['id']);
    this.settings_id = this.data['settings'].map((item) => item['id']);

    const defaultDisabled = !this.data['default']['is_active'];

    this.formGroup = this.fb.group({
      'default': this.fb.group({
        'is_active': [this.data['default']['is_active']],
        'voznagr_agenta': [{value: this.data['default']['voznagr_agenta'], disabled: defaultDisabled}],
        'voznagr_agenta_from_ddu': [{value: this.data['default']['voznagr_agenta_from_ddu'], disabled: defaultDisabled}],
        'dolya_subagenta_db': [{value: (this.data['default']['dolya_subagenta_db']
            * this.data['default']['voznagr_agenta']).toFixed(5), disabled: defaultDisabled}],
        'dolya_subagenta_agd': [{value: (this.data['default']['dolya_subagenta_agd']
            * this.data['default']['voznagr_agenta_from_ddu']).toFixed(5), disabled: defaultDisabled}],
        'srok_realisation': [{value: this.data['default']['srok_realisation'], disabled: defaultDisabled}],
        'unique_term_days': [{value: this.data['default']['unique_term_days'], disabled: defaultDisabled}],
        'lead_transfer_capability': [{value: this.data['default']['lead_transfer_capability'], disabled: defaultDisabled}],
        'lead_transfer_sub_percent_db': [{value: this.data['default']['lead_transfer_sub_percent_db'], disabled: defaultDisabled}],
        'lead_transfer_sub_percent_agd': [{value: this.data['default']['lead_transfer_sub_percent_agd'], disabled: defaultDisabled}],
      }),
      'complexes': this.fb.array(this.data['complexes'].map((item) => {
        return this.fb.group({
          'id': item['id'],
          'realty_id': item['realty_id'],
          'caption': item['caption'],
          'developer_id': item['developer_id'],
          'is_checked': item['is_checked'],
        });
      })),
      'settings': this.fb.array(this.data['settings'].map((item) => {
        const dis = !item['is_active'];
        return this.fb.group({
          'id': item['id'],
          'caption': item['caption'],
          'developer_id': item['developer_id'],
          'is_active': item['is_active'],
          'voznagr_agenta': [{value: item['voznagr_agenta'], disabled: dis}],
          'voznagr_agenta_from_ddu': [{value: item['voznagr_agenta_from_ddu'], disabled: dis}],
          'dolya_subagenta_db': [{value: (item['dolya_subagenta_db'] * item['voznagr_agenta']).toFixed(5), disabled: dis}],
          'dolya_subagenta_agd': [{value: (item['dolya_subagenta_agd'] * item['voznagr_agenta_from_ddu']).toFixed(5), disabled: dis}],
          'srok_realisation': [{value: item['srok_realisation'], disabled: dis}],
          'unique_term_days': [{value: item['unique_term_days'], disabled: dis}],
          'lead_transfer_capability': [{value: item['lead_transfer_capability'], disabled: dis}],
          'lead_transfer_sub_percent_db': [{value: item['lead_transfer_sub_percent_db'], disabled: dis}],
          'lead_transfer_sub_percent_agd': [{value: item['lead_transfer_sub_percent_agd'], disabled: dis}],
        });
      })),
    });

    // Блокировка/разблокировка полей default при смене is_active
    this.subscriptions.push(
      this.defaults.get('is_active').valueChanges.pipe(debounceTime(100))
        .subscribe((val) => {
          this.setDisabled(this.defaults, val);
          // Смена default.is_active влияет на активность всех ЖК с is_checked=true
          this.updateDeveloperGroups();
        })
    );

    // Блокировка/разблокировка полей для существующих settings
    this.settings.controls.forEach((item) => {
      this.subscriptions.push(
        item.get('is_active').valueChanges.subscribe((val) => {
          this.setDisabled(item, val);
          // Смена is_active конкретного ЖК влияет на группировку застройщиков
          this.updateDeveloperGroups();
        })
      );
    });

    // Изменение чекбоксов "использовать общие настройки" (complexes)
    this.subscriptions.push(
      this.complexes.valueChanges.pipe(debounceTime(100))
        .subscribe((data) => {
          // Сначала синхронизируем массив settings (добавляем/удаляем строки)
          data.forEach((item) => {
            const index = this.settings_id.indexOf(item.id);
            if (item.is_checked && index !== -1) {
              this.settings_id.splice(index, 1);
              this.settings.removeAt(index);
            }
            if (!item.is_checked && index === -1) {
              this.settings_id.push(item.id);
              this.appendSettings(item);
            }
          });
          // Затем пересчитываем группы застройщиков (settings уже актуален)
          this.updateDeveloperGroups();
        })
    );
  }

  /**
   * Определяет, считается ли ЖК "принимающим на реализацию":
   * - is_checked=true  → используют общие настройки → берём default.is_active
   * - is_checked=false → используют свои настройки  → берём is_active из settings
   */
  private isComplexAcceptingRealization(complexControl: any): boolean {
    const isChecked = complexControl.get('is_checked')?.value;

    if (isChecked) {
      // Общие настройки — смотрим на default.is_active
      return !!this.defaults.get('is_active')?.value;
    } else {
      // Свои настройки — находим соответствующую запись в settings
      const complexId = complexControl.get('id')?.value;
      const settingControl = this.settings.controls.find(
        (s) => s.get('id')?.value === complexId
      );
      return !!settingControl?.get('is_active')?.value;
    }
  }

  /**
   * Пересчитывает разбивку застройщиков на две группы.
   * Застройщик попадает в active, если хотя бы один его ЖК принимает на реализацию.
   */
  private updateDeveloperGroups(): void {
    const developers: Developer[] = this.data['developers'] ?? [];

    if (!developers.length) {
      this.developerGroups = { active: [], inactive: [] };
      return;
    }

    // Определяем множество developer_id, у которых есть хотя бы один активный ЖК
    const activeDeveloperIds = new Set<any>();

    this.complexes.controls.forEach((complexControl) => {
      if (this.isComplexAcceptingRealization(complexControl)) {
        const devId = complexControl.get('developer_id')?.value;
        if (devId != null) {
          activeDeveloperIds.add(devId);
        }
      }
    });

    this.developerGroups = {
      active:   developers.filter((dev) => activeDeveloperIds.has(dev.id)),
      inactive: developers.filter((dev) => !activeDeveloperIds.has(dev.id)),
    };
  }

  ngOnInit() {
    this.updateDeveloperGroups();

    this.leadValidators(this.defaults.get('lead_transfer_capability').value);
    this.subscriptions.push(
      this.defaults.get('lead_transfer_capability').valueChanges
        .subscribe((value) => this.leadValidators(value))
    );
  }

  leadValidators(value) {
    if (!value) {
      this.defaults.get('lead_transfer_sub_percent_db').patchValue(null);
      this.defaults.get('lead_transfer_sub_percent_agd').patchValue(null);
    }
  }

  setDisabled(form, is_active) {
    const method = is_active ? 'enable' : 'disable';
    [
      'voznagr_agenta', 'voznagr_agenta_from_ddu',
      'dolya_subagenta_db', 'dolya_subagenta_agd',
      'srok_realisation', 'unique_term_days',
      'lead_transfer_capability',
      'lead_transfer_sub_percent_db', 'lead_transfer_sub_percent_agd',
    ].forEach((field) => form.get(field)?.[method]());
  }

  public get complexes(): UntypedFormArray {
    return this.formGroup.get('complexes') as UntypedFormArray;
  }

  public get settings(): UntypedFormArray {
    return this.formGroup.get('settings') as UntypedFormArray;
  }

  public get defaults(): UntypedFormGroup {
    return this.formGroup.get('default') as UntypedFormGroup;
  }

  appendSettings(data) {
    const dis = !this.defaults.getRawValue().is_active;
    const form = this.fb.group({
      'id': data.id,
      'caption': data.caption,
      'developer_id': data.developer_id,
      'is_active': this.defaults.getRawValue().is_active,
      'voznagr_agenta': [{value: this.defaults.getRawValue().voznagr_agenta || null, disabled: dis}],
      'voznagr_agenta_from_ddu': [{value: this.defaults.getRawValue().voznagr_agenta_from_ddu || null, disabled: dis}],
      'dolya_subagenta_db': [{value: this.defaults.getRawValue().dolya_subagenta_db || null, disabled: dis}],
      'dolya_subagenta_agd': [{value: this.defaults.getRawValue().dolya_subagenta_agd || null, disabled: dis}],
      'srok_realisation': [{value: this.defaults.getRawValue().srok_realisation || null, disabled: dis}],
      'unique_term_days': [{value: this.defaults.getRawValue().unique_term_days || null, disabled: dis}],
      'lead_transfer_capability': [{value: this.defaults.getRawValue().lead_transfer_capability || null, disabled: dis}],
      'lead_transfer_sub_percent_db': [{value: this.defaults.getRawValue().lead_transfer_sub_percent_db || null, disabled: dis}],
      'lead_transfer_sub_percent_agd': [{value: this.defaults.getRawValue().lead_transfer_sub_percent_agd || null, disabled: dis}],
    });
    this.settings.push(form);
    this.subscriptions.push(
      form.get('is_active').valueChanges.subscribe((val) => {
        this.setDisabled(form, val);
        this.updateDeveloperGroups();
      })
    );
  }

  saveForm() {
    if (!this.formGroup.valid) {
      this.onError('Заполните обязательные поля');
      return false;
    }
    this.loading = true;

    const settingObj = this.formGroup.get('settings').value.map((el) => Object.assign({}, el));

    const data = {
      'setting_id': this.feed_id,
      'data': {
        'default': Object.assign({}, this.formGroup.get('default').value),
        'complexes': Object.assign({}, this.formGroup.get('complexes').value),
        'settings': settingObj,
      },
    };

    let wrongPercent = true;
    if (
      parseFloat(data['lead_transfer_sub_percent_db']) > (parseFloat(data['dolya_subagenta_db']) || 0) ||
      parseFloat(data['lead_transfer_sub_percent_agd']) > (parseFloat(data['dolya_subagenta_agd']) || 0)
    ) {
      wrongPercent = false;
    }

    data['data']['default']['dolya_subagenta_db'] = this.partRate.transform(
      data['data']['default']['voznagr_agenta'], data['data']['default']['dolya_subagenta_db']);
    data['data']['default']['dolya_subagenta_agd'] = this.partRate.transform(
      data['data']['default']['voznagr_agenta_from_ddu'], data['data']['default']['dolya_subagenta_agd']);

    if (wrongPercent) {
      for (let i = 0; i < this.settings.controls.length; i++) {
        const s = data['data']['settings'][i];
        if (s['lead_transfer_sub_percent_db']) { s['lead_transfer_sub_percent_db'] = s['lead_transfer_sub_percent_db'].toString().replace(',', '.'); }
        if (s['dolya_subagenta_db'])            { s['dolya_subagenta_db']            = s['dolya_subagenta_db'].toString().replace(',', '.'); }
        if (s['lead_transfer_sub_percent_agd']) { s['lead_transfer_sub_percent_agd'] = s['lead_transfer_sub_percent_agd'].toString().replace(',', '.'); }
        if (s['dolya_subagenta_agd'])           { s['dolya_subagenta_agd']           = s['dolya_subagenta_agd'].toString().replace(',', '.'); }

        if (
          parseFloat(s['lead_transfer_sub_percent_db']) > (parseFloat(s['dolya_subagenta_db']) || 0) ||
          parseFloat(s['lead_transfer_sub_percent_agd']) > (parseFloat(s['dolya_subagenta_agd']) || 0)
        ) {
          wrongPercent = false;
          break;
        }

        s['dolya_subagenta_db']  = this.partRate.transform(s['voznagr_agenta'], s['dolya_subagenta_db']);
        s['dolya_subagenta_agd'] = this.partRate.transform(s['voznagr_agenta_from_ddu'], s['dolya_subagenta_agd']);

        if (s['dolya_subagenta_db'] > 1 || s['dolya_subagenta_agd'] > 1) {
          wrongPercent = false;
          break;
        }
      }
    }

    if (data['data']['default']['dolya_subagenta_db'] > 1 || data['data']['default']['dolya_subagenta_agd'] > 1 || !wrongPercent) {
      this.notify.notifyError('Ошибка', 'Неверный процент субагента', 3000);
      this.loading = false;
    } else {
      this.http.post('Import.realization_update', data).subscribe(
        (response) => { this.onSuccess(response); this.loading = false; },
        (error)    => { this.onError(error);       this.loading = false; }
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onSuccess(response?) {
    this.notify.notifySuccess('Поздравляем', 'Настройки успешно сохранена', 3000);
  }

  onError(error) {
    this.notify.notifyError('Ошибка', error, 5000);
  }
}
